import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
    DEFAULT_FC_TRANSFER_DIRECTION,
    FCTransferSortConfig,
    FCTransferTransformConfig,
    mergeFCTransferListFiltersWithRequiredContext,
    normalizeFCTransferDirection,
} from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import { FCTransfersFilters } from 'components/warehouse/components/fcTransfers/FCTransfersFilters';
import { FCTransfersTable } from 'components/warehouse/components/fcTransfers/FCTransfersTable';
import {
    useCancelFCTransfer,
    useFCTransfersSearch,
} from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi } from 'shared';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Drops facility ids before URL sync — they are always derived from the logged-in user + direction.
 *
 * @param {Object} filters
 * @returns {Object}
 */
function toUrlSyncableFilters(filters) {
    const { destinationFacilityId, sourceFacilityId, ...rest } = filters;
    return rest;
}

/**
 * @param {string} searchString
 * @param {number | string | null | undefined} currentFacilityId
 * @returns {Object}
 */
function buildStateFromSearch(searchString, currentFacilityId) {
    const parsed = fromUrl(searchString, FCTransferTransformConfig);
    const { destinationFacilityId: _d, sourceFacilityId: _s, ...rest } = parsed;
    const merged = mergeFCTransferListFiltersWithRequiredContext(rest, {
        currentFacilityId,
        direction: normalizeFCTransferDirection(rest.direction),
    });
    const pageSize = parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE;
    return { ...merged, pageSize };
}

/**
 * FC Transfers list: URL-synced filters, direction tabs, cursor pagination, and table (tech spec §3.1, §5, §7.2).
 */
export function ViewFCTransfers() {
    const history = useHistory();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const currentFacilityId = user?.facilityID;

    const [filters, setFilters] = useState(() =>
        buildStateFromSearch(typeof window !== 'undefined' ? window.location.search : '', currentFacilityId),
    );
    const [currentCursor, setCurrentCursor] = useState(undefined);
    const [cursorHistory, setCursorHistory] = useState([]);
    const storedTotalCountRef = useRef(undefined);

    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;

    useEffect(() => {
        // pushToUrl uses window.history.pushState (FiltersURLHelper) — it does not update React Router's
        // location, so prefer window.location.search for the live query string on this route.
        const liveSearch =
            (typeof window !== 'undefined' && window.location.search ? window.location.search : '') ||
            (typeof location.search === 'string' ? location.search : '');
        setFilters(buildStateFromSearch(liveSearch, currentFacilityId));
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
    }, [location.search, currentFacilityId, location.pathname]);

    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, FCTransferTransformConfig);
        if (!transformed.pageSize) {
            transformed.pageSize = DEFAULT_PAGE_SIZE;
        }
        return transformed;
    }, [filters]);

    const paginatedFilters = useMemo(
        () => ({
            ...apiFilters,
            cursor: currentCursor,
            ...(currentCursor !== undefined &&
                storedTotalCountRef.current !== undefined && {
                    totalCount: storedTotalCountRef.current,
                }),
        }),
        [apiFilters, currentCursor],
    );

    const paginatedQuery = useFCTransfersSearch(paginatedFilters, {
        enabled: currentFacilityId !== undefined && currentFacilityId !== null && currentFacilityId !== '',
    });

    const cancelFCTransferMutation = useCancelFCTransfer({
        onSuccess: () => {
            toast.success('Transfer cancelled');
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to cancel transfer');
        },
    });

    useEffect(() => {
        if (paginatedQuery.data?.pagination?.totalCount !== undefined) {
            storedTotalCountRef.current = paginatedQuery.data.pagination.totalCount;
        }
    }, [paginatedQuery.data?.pagination?.totalCount]);

    const { data, error, isFetching, isLoading } = paginatedQuery;
    const rows = useMemo(() => data?.rows ?? [], [data]);
    const pagination = useMemo(() => data?.pagination ?? {}, [data]);
    const totalCount = pagination.totalCount ?? storedTotalCountRef.current ?? 0;
    const errorMessage = error?.message ?? '';

    const getFiltersForBackNav = useCallback(() => {
        const fromWindow = typeof window !== 'undefined' && window?.location?.search ? window.location.search : '';
        const fromRouter = typeof location?.search === 'string' && location.search ? location.search : '';
        // Must prefer window: pushToUrl updates via pushState, so React Router's location.search is stale.
        return fromWindow || fromRouter || '';
    }, [location.search]);

    const handleFilterChange = useCallback(
        (nextFilters) => {
            const updatedState = {
                ...nextFilters,
                pageSize: filters.pageSize || DEFAULT_PAGE_SIZE,
                ...(filters.sortField && { sortField: filters.sortField }),
                ...(filters.sortDir && { sortDir: filters.sortDir }),
            };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(updatedState);
            pushToUrl(removeEmptyFilters(toUrlSyncableFilters(updatedState)), FCTransferTransformConfig, {
                replace: false,
            });
        },
        [filters.pageSize, filters.sortField, filters.sortDir],
    );

    const handleNextPage = useCallback(() => {
        if (pagination.hasMore && pagination.nextCursor) {
            setCursorHistory((prev) => [...prev, currentCursor]);
            setCurrentCursor(pagination.nextCursor);
        }
    }, [pagination, currentCursor]);

    const handlePreviousPage = useCallback(() => {
        if (cursorHistory.length > 0) {
            const newHistory = [...cursorHistory];
            const previousCursor = newHistory.pop();
            setCursorHistory(newHistory);
            setCurrentCursor(previousCursor);
        }
    }, [cursorHistory]);

    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            const nextState = { ...filters, pageSize: newPageSize };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(toUrlSyncableFilters(nextState)), FCTransferTransformConfig, {
                replace: true,
            });
        },
        [filters],
    );

    const sorting = useMemo(() => {
        const apiField = filters.sortField;
        if (!apiField) return [];
        const columnId = Object.entries(FCTransferSortConfig.sortableFields).find(
            ([, cfg]) => cfg.apiField === apiField,
        )?.[0];
        if (!columnId) return [];
        return [{ id: columnId, desc: filters.sortDir === 'desc' }];
    }, [filters.sortField, filters.sortDir]);

    const handleSortingChange = useCallback(
        (updater) => {
            const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
            const sortEntry = nextSorting?.[0];
            const sortField = sortEntry ? FCTransferSortConfig.sortableFields[sortEntry.id]?.apiField : undefined;
            let sortDir;
            if (sortEntry) {
                sortDir = sortEntry.desc ? 'desc' : 'asc';
            } else {
                sortDir = undefined;
            }

            const { sortDir: _prevSd, sortField: _prevSf, ...restFilters } = filters;
            const nextState = sortField ? { ...filters, sortField, sortDir } : restFilters;
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(toUrlSyncableFilters(nextState)), FCTransferTransformConfig, {
                replace: true,
            });
        },
        [filters, sorting],
    );

    // List rows use `id` only (GET search payload from FcTransferSearchResponse::fromRawRows).
    const resolveTransferId = useCallback((row) => {
        const id = row?.id;
        return id === undefined || id === null ? undefined : id;
    }, []);

    const handleViewFCTransfer = useCallback(
        (row) => {
            const id = resolveTransferId(row);
            if (id === undefined || id === null) return;
            const navState = { fcTransfersFilters: getFiltersForBackNav() };
            history.push(`/warehouse/fc-transfers/${encodeURIComponent(String(id))}`, navState);
        },
        [getFiltersForBackNav, history, resolveTransferId],
    );

    const handleEditFCTransfer = useCallback(
        (row) => {
            const id = resolveTransferId(row);
            if (id === undefined || id === null) return;
            const navState = {
                startLineItemsEdit: true,
                fcTransfersFilters: getFiltersForBackNav(),
            };
            history.push(`/warehouse/fc-transfers/${encodeURIComponent(String(id))}`, navState);
        },
        [getFiltersForBackNav, history, resolveTransferId],
    );

    const handleAcceptFCTransfer = useCallback(
        (row) => {
            const id = resolveTransferId(row);
            if (id === undefined || id === null) return;
            const navState = { fcTransfersFilters: getFiltersForBackNav() };
            history.push(`/warehouse/accept-fc-transfer/${encodeURIComponent(String(id))}`, navState);
        },
        [getFiltersForBackNav, history, resolveTransferId],
    );

    const handleCancelFCTransfer = useCallback(
        (row) => {
            const id = resolveTransferId(row);
            if (id === undefined || id === null) return;
            // eslint-disable-next-line no-alert -- destructive action needs explicit operator confirmation
            if (!window.confirm('Cancel this transfer? This cannot be undone.')) return;
            cancelFCTransferMutation.mutate(id);
        },
        [cancelFCTransferMutation, resolveTransferId],
    );

    const handleCreateFCTransfer = useCallback(() => {
        const navState = { fcTransfersFilters: getFiltersForBackNav() };
        history.push('/warehouse/create-fc-transfer', navState);
    }, [getFiltersForBackNav, history]);

    const direction = normalizeFCTransferDirection(filters.direction ?? DEFAULT_FC_TRANSFER_DIRECTION);

    const filterValuesForUI = useMemo(() => {
        const {
            destinationFacilityId: _df,
            direction: _dir,
            pageSize: _ps,
            sortDir: _sd,
            sortField: _sf,
            sourceFacilityId: _s,
            ...filterOnly
        } = filters;
        return filterOnly;
    }, [filters]);

    const currentPage = cursorHistory.length + 1;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-semibold">FC Transfers</h1>
                    <Button size="sm" onClick={handleCreateFCTransfer}>
                        Create Transfer
                    </Button>
                </div>
                <div className="mt-4">
                    <FCTransfersFilters
                        key={location.search}
                        currentFacilityId={currentFacilityId}
                        direction={direction}
                        initialValues={filterValuesForUI}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-6 pt-4">
                {errorMessage ? (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {errorMessage}
                    </div>
                ) : null}
                <FCTransfersTable
                    currentPage={currentPage}
                    data={rows}
                    direction={direction}
                    emptyMessage="No FC transfers found."
                    hasNextPage={pagination.hasMore}
                    hasPrevious={cursorHistory.length > 0}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onAcceptFCTransfer={handleAcceptFCTransfer}
                    onCancelFCTransfer={handleCancelFCTransfer}
                    onEditFCTransfer={handleEditFCTransfer}
                    onNext={handleNextPage}
                    onPageSizeChange={handlePageSizeChange}
                    onPrevious={handlePreviousPage}
                    onSortingChange={handleSortingChange}
                    onViewFCTransfer={handleViewFCTransfer}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    sorting={sorting}
                    totalCount={totalCount}
                    totalPages={totalPages}
                />
            </div>
        </div>
    );
}

ViewFCTransfers.displayName = 'ViewFCTransfers';
